/**
 * @file ProductShowcase.jsx
 * @description The one product showcase for the public pages: real screenshots (sample data, no real people) inside
 *   a browser frame, with tabs, a caption per tab and progress dots. Used by the agency page (default tabs below:
 *   openings and the provider map, applicants by fit, Case Fit, must haves, interviews, notebook), its Pro preview
 *   (light tone), the ABA Case Finder page (provider screens) and the Pay & Hours tracker showcase.
 *   Images live in public/marketing/ (WebP, 2x); only the active one is in the page.
 *   Frames auto-advance and pause on hover or after a tap.
 * @module components/ProductShowcase
 */
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MapPinIcon, UsersIcon, TargetIcon, ClipboardIcon, CalendarIcon, NoteIcon, LockIcon, BriefcaseIcon } from "./Icons";
import { SAMPLE_TAG } from "./AgencyVisuals";

const SHOT_BG = "#F4F4F2"; // the screenshots' own page color, so "contain" frames have no visible edge

/**
 * fit: "cover" fills the frame from the top left (full screens); "contain" shows the whole component centered.
 * Copy comes from agencyShowcase.tab / caption / alt.<key> unless a tab names its own labelKey / captionKey / altKey.
 * badge: a short tag beside the label (the Pro screens say PRO).
 * Panel: a drawn sample screen (a component taking { isMobile }) instead of a screenshot; the frame wraps it in
 *   role="img" with the tab's alt text, so it needs no src/w/h. vars: values for the tab's caption and alt text.
 * phoneX: on phones a full desktop screen is too small to read, so "cover" shots show at twice the frame width,
 *   starting this far across the image (0 = left edge, 0.5 = right half). Pick where the screen's content sits.
 */
export const SHOWCASE_TABS = [
  { id: "openings",   key: "openings",  Icon: MapPinIcon,    src: "/marketing/agency/openings.webp",           w: 1960, h: 1225, fit: "cover", phoneX: 0 },
  { id: "staff",      key: "staff",     Icon: BriefcaseIcon, src: "/marketing/provider/bcba-position.webp",    w: 1960, h: 1225, fit: "cover", phoneX: 0.18 },
  { id: "applicants", key: "applicants", Icon: UsersIcon,    src: "/marketing/agency/applicants.webp",         w: 1960, h: 1225, fit: "cover", phoneX: 0.3 },
  { id: "fit",        key: "fit",       Icon: TargetIcon,    src: "/marketing/agency/fit-card.webp",           w: 1600, h: 1172, fit: "contain" },
  { id: "posting",    key: "posting",   Icon: ClipboardIcon, src: "/marketing/agency/posting-must-haves.webp", w: 1520, h: 1100, fit: "contain" },
  { id: "interviews", key: "interviews", Icon: CalendarIcon, src: "/marketing/agency/interviews.webp",         w: 1960, h: 1263, fit: "cover", phoneX: 0.05 },
  { id: "notebook",   key: "notebook",  Icon: NoteIcon,      src: "/marketing/agency/notebook.webp",           w: 1960, h: 1373, fit: "contain" },
];

const copyKeys = (tab) => ({
  label:   tab.labelKey   || `agencyShowcase.tab.${tab.key}`,
  caption: tab.captionKey || `agencyShowcase.caption.${tab.key}`,
  alt:     tab.altKey     || `agencyShowcase.alt.${tab.key}`,
});

/** Pill, caption and dot colors for a dark section (the showcase) or a light one (the plans). */
const TONES = {
  dark: {
    pillOn: { background: "#fff", color: "#1E1B4B", border: "transparent" },
    pillOff: { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.8)", border: "rgba(255,255,255,0.22)" },
    caption: "rgba(255,255,255,0.88)", dotOn: "#fff", dotOff: "rgba(255,255,255,0.35)", shadow: "0 30px 80px rgba(15,16,36,0.35), 0 10px 30px rgba(15,16,36,0.22)",
  },
  light: {
    pillOn: { background: "#1E1B4B", color: "#fff", border: "transparent" },
    pillOff: { background: "#fff", color: "#3D3A52", border: "rgba(226,232,240,0.95)" },
    caption: "#475569", dotOn: "#1E1B4B", dotOff: "#CBD5E1", shadow: "0 18px 48px rgba(15,23,42,0.12), 0 4px 14px rgba(15,23,42,0.08)",
  },
};

// ── Browser chrome wrapper ─────────────────────────────────────────────────────
function BrowserChrome({ children, isMobile, shadow }) {
  return (
    <div style={{ borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid rgba(226,232,240,0.9)", boxShadow: shadow }}>
      <div style={{ height: 38, background: "#F1F3F7", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", padding: "0 14px", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["#FF5F57", "#FEBC2E", "#28C840"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
        </div>
        <div style={{ flex: 1, maxWidth: 280, margin: "0 auto", height: 22, borderRadius: 6, background: "#fff", border: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <LockIcon size={9} color="#94A3B8" />
          <span style={{ fontSize: 11, color: "#64748B", fontFamily: "'JetBrains Mono', monospace" }}>abacases.com/dashboard</span>
        </div>
        <div style={{ width: isMobile ? 0 : 40 }} />
      </div>
      <div style={{ position: "relative", height: isMobile ? 300 : 520, overflow: "hidden", background: SHOT_BG }}>{children}</div>
    </div>
  );
}

function Shot({ tab, alt, isMobile }) {
  if (tab.Panel) {
    return <div role="img" aria-label={alt} data-testid={`showcase-panel-${tab.id}`} style={{ height: "100%" }}><tab.Panel isMobile={isMobile} /></div>;
  }
  const cover = tab.fit === "cover";
  if (isMobile && cover) {
    const x = Math.max(0, Math.min(0.5, tab.phoneX ?? 0));
    return (
      <img
        src={tab.src} alt={alt} width={tab.w} height={tab.h} decoding="async" data-testid={`showcase-shot-${tab.id}`}
        style={{ position: "absolute", top: 0, left: `${-x * 200}%`, width: "200%", maxWidth: "none", height: "auto", display: "block" }}
      />
    );
  }
  return (
    <img
      src={tab.src} alt={alt} width={tab.w} height={tab.h} decoding="async" data-testid={`showcase-shot-${tab.id}`}
      style={{
        display: "block", width: "100%", height: "100%", boxSizing: "border-box",
        objectFit: cover ? "cover" : "contain", objectPosition: cover ? "left top" : "center",
        padding: cover ? 0 : 16,
      }}
    />
  );
}

// ── Main showcase ──────────────────────────────────────────────────────────────
/**
 * @param {{ isMobile: boolean, accent?: string, tabs?: Array, tone?: "dark"|"light", sampleTag?: boolean, testId?: string }} props
 *   sampleTag: show the "Sample" tag on the frame (where no section line already says the data is sample).
 */
export default function ProductShowcase({ isMobile, accent = "#4F46E5", tabs = SHOWCASE_TABS, tone = "dark", sampleTag = false, testId = "showcase" }) {
  const { t } = useTranslation();
  const c = TONES[tone] || TONES.dark;
  const [active, setActive] = useState(tabs[0].id);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const resumeRef = useRef(null);
  const tabRowRef = useRef(null);
  const current = tabs.find(x => x.id === active) || tabs[0];
  const currentKeys = copyKeys(current);

  // Keep the active pill visible when the row scrolls (phones only; scrolls the row, not the page).
  useEffect(() => {
    const row = tabRowRef.current;
    if (!isMobile || !row) return;
    const btn = row.querySelector(`[data-tab-id="${active}"]`);
    if (btn && typeof row.scrollTo === "function") {
      // Position within the row's scrolled content (offsetLeft is measured from the page when the row is not positioned).
      const left = btn.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
      row.scrollTo({ left: Math.max(0, left - (row.clientWidth - btn.offsetWidth) / 2), behavior: "smooth" });
    }
  }, [active, isMobile]);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setInterval(() => setActive(a => tabs[(tabs.findIndex(x => x.id === a) + 1) % tabs.length].id), 5200);
    return () => clearInterval(timerRef.current);
  }, [paused, tabs]);

  useEffect(() => () => clearTimeout(resumeRef.current), []);

  const select = (id) => {
    setActive(id);
    setPaused(true);
    clearTimeout(resumeRef.current);
    resumeRef.current = setTimeout(() => setPaused(false), 9000);
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", position: "relative" }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <style>{`@keyframes showcaseFrameIn { from { opacity: 0; transform: translateY(10px) scale(0.995); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>

      {/* Tab switcher. On phones the pills scroll sideways on one row (never wrap to an orphan second row). */}
      <div ref={tabRowRef} data-testid={`${testId}-tabs`} style={isMobile
        ? { display: "flex", justifyContent: tabs.length > 2 ? "flex-start" : "center", gap: 6, marginBottom: 18, flexWrap: "nowrap", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", padding: "2px 4px 4px" }
        : { display: "flex", justifyContent: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {tabs.map(tab => {
          const on = active === tab.id;
          const pill = on ? c.pillOn : c.pillOff;
          // Few tabs fit on a phone, so they keep their labels; the long workflow row shows only the active label.
          const showLabel = !isMobile || on || tabs.length <= 3;
          return (
            <button key={tab.id} type="button" data-tab-id={tab.id} aria-pressed={on} onClick={() => select(tab.id)} style={{
              flex: "0 0 auto", whiteSpace: "nowrap",
              display: "flex", alignItems: "center", gap: 6,
              minHeight: isMobile ? 44 : undefined, padding: isMobile ? "7px 12px" : "8px 14px", borderRadius: 100,
              border: `1.5px solid ${pill.border}`, background: pill.background, color: pill.color,
              fontSize: isMobile ? 12 : 12.5, fontWeight: 700, cursor: "pointer",
              fontFamily: "'Inter', sans-serif", transition: "all 0.18s ease",
              boxShadow: on ? "0 6px 18px rgba(0,0,0,0.18)" : "none",
            }}>
              <tab.Icon size={14} color={on ? (tone === "light" ? "#fff" : accent) : pill.color} />
              {showLabel && t(copyKeys(tab).label)}
              {tab.badge && showLabel && (
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", color: on && tone === "light" ? "#1E1B4B" : "#6D28D9", background: on && tone === "light" ? "#EDE9FE" : "#F3F0FF", borderRadius: 99, padding: "1px 6px" }}>{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      <BrowserChrome isMobile={isMobile} shadow={c.shadow}>
        {sampleTag && <span data-testid="sample-badge" style={{ ...SAMPLE_TAG, left: "auto", right: 10, top: "auto", bottom: 10, zIndex: 2, boxShadow: "0 1px 3px rgba(15,23,42,0.12)" }}>{t("agencyVisuals.sample")}</span>}
        <div key={current.id} style={{ position: "relative", height: "100%", animation: "showcaseFrameIn 0.5s ease both" }}>
          <Shot tab={current} alt={t(currentKeys.alt, current.vars)} isMobile={isMobile} />
        </div>
      </BrowserChrome>

      <p data-testid={`${testId}-caption`} style={{ textAlign: "center", fontSize: isMobile ? 14 : 15, fontWeight: 600, color: c.caption, margin: "18px auto 0", maxWidth: 620, lineHeight: 1.45 }}>
        {t(currentKeys.caption, current.vars)}
      </p>

      {/* Progress dots. On phones each dot sits in a 44px-tall tap area (the visible dot stays small). */}
      <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? 0 : 8, marginTop: isMobile ? -4 : 14 }}>
        {tabs.map(tab => (
          <button key={tab.id} type="button" onClick={() => select(tab.id)} aria-label={t(copyKeys(tab).label)} aria-current={active === tab.id ? "true" : undefined}
            style={{ border: "none", background: "none", cursor: "pointer", padding: isMobile ? "18px 18px" : 0, display: "inline-flex", alignItems: "center" }}>
            <span aria-hidden="true" style={{ display: "block", width: active === tab.id ? 26 : 8, height: 8, borderRadius: 100, background: active === tab.id ? c.dotOn : c.dotOff, transition: "all 0.25s ease" }} />
          </button>
        ))}
      </div>
    </div>
  );
}
